import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import { Textarea } from './ui/textarea';
import { getErrorMessage } from '../lib/utils';
import AnaliseTemposOF from './AnaliseTemposOF';
import { useOF } from '../context/OFContext';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter 
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from './ui/select';
import { Checkbox } from './ui/checkbox';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { 
  Package, Plus, Check, Clock, FileText, Download, Trash2, QrCode,
  AlertCircle, CheckCircle2, Loader2, X, Merge, Archive, Tag, Layers, ArrowRight,
  Scissors, Undo2, Search, Filter, History, ChevronRight, ChevronDown, ChevronUp, ArrowLeft,
  Bell, MessageSquare, Users, Send, Settings, PieChart, Edit, Lock, Eye
} from 'lucide-react';

// Modais extraídos
import { 
  ModalAvancarLote, 
  ModalDividirLote, 
  ModalRetrocederLote, 
  ModalEditarQuantidade,
  CategoriaCard
} from './gestor/lotes';
import GestaoLotesHistorico from './GestaoLotesHistorico';
import GestaoLotesAlertarOps from './GestaoLotesAlertarOps';
import ModalOperacoesLote from './gestor/lotes/ModalOperacoesLote';
import LotesAprovadosLista from './LotesAprovadosLista';
import GestaoLotesHeader from './GestaoLotesHeader';
import LotesPendentesLista from './LotesPendentesLista';
import CriarLoteForm from './CriarLoteForm';
import CriarLoteArranjoModal from './anomalias/CriarLoteArranjoModal';
import TerceirizacaoLoteModal from './TerceirizacaoLoteModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function GestaoLotes({ ofId, numeroOf, quantidadeTotal, marca, dataEntrega, categoriasEspeciais = [], onClose, onUpdate, onEditOperacoes, fullscreen = true }) {
  const navigate = useNavigate();
  
  // Funções do contexto para gestão de lotes
  const { 
    categorias: categoriasContext,
    operacoes: operacoesContext,
    operadores,
    loadLotes: loadLotesContext,
    createLote: createLoteContext,
    aprovarLote: aprovarLoteContext,
    deleteLote: deleteLoteContext,
    unirLotes: unirLotesContext,
    avancarLote: avancarLoteContext,
    dividirLote: dividirLoteContext,
    retrocederLote: retrocederLoteContext,
    updateLoteObservacoes: updateLoteObsContext,
    getLotePdfUrl
  } = useOF();

  const [lotes, setLotes] = useState([]);
  const [resumo, setResumo] = useState({});
  const [categorias, setCategorias] = useState([]);
  const [categoriasFiltradas, setCategoriasFiltradas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNovoLote, setShowNovoLote] = useState(false);
  const [showCriarLoteArranjo, setShowCriarLoteArranjo] = useState(false);
  const [terceirizarLote, setTerceirizarLote] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aprovando, setAprovando] = useState(null);
  
  // Novo estado para criação de lote melhorada
  // Para união: lotes selecionados com quantidades

  // Estado para avançar lote
  const [showAvancarLote, setShowAvancarLote] = useState(false);
  const [loteParaAvancar, setLoteParaAvancar] = useState(null);
  const [categoriaDestino, setCategoriaDestino] = useState('');
  const [quantidadeAvancar, setQuantidadeAvancar] = useState('');
  const [observacoesAvanco, setObservacoesAvanco] = useState('');
  const [erroAvancar, setErroAvancar] = useState('');

  // Estado para dividir lote - AVANÇADO
  const [showDividirLote, setShowDividirLote] = useState(false);
  const [loteParaDividir, setLoteParaDividir] = useState(null);
  const [categoriaDivisao, setCategoriaDivisao] = useState('');
  const [numeroLotesDivisao, setNumeroLotesDivisao] = useState(1);
  const [quantidadePorLoteDivisao, setQuantidadePorLoteDivisao] = useState('');
  const [observacoesDivisao, setObservacoesDivisao] = useState('');
  const [erroDividir, setErroDividir] = useState('');

  // Estado para retroceder lote
  const [showRetrocederLote, setShowRetrocederLote] = useState(false);
  const [loteParaRetroceder, setLoteParaRetroceder] = useState(null);
  const [categoriaRetrocesso, setCategoriaRetrocesso] = useState('');
  const [quantidadeRetroceder, setQuantidadeRetroceder] = useState('');
  const [motivoRetrocesso, setMotivoRetrocesso] = useState('');

  // Estado para editar observações do lote
  const [showEditObservacoes, setShowEditObservacoes] = useState(false);
  const [loteParaEditarObs, setLoteParaEditarObs] = useState(null);
  const [novasObservacoes, setNovasObservacoes] = useState('');

  // Estado para filtros e pesquisa
  const [filtroCategoria, setFiltroCategoria] = useState('todas');
  const [pesquisaLote, setPesquisaLote] = useState('');

  // Estado para histórico de lote
  const [showHistorico, setShowHistorico] = useState(false);
  const [loteHistorico, setLoteHistorico] = useState(null);
  const [registosHistorico, setRegistosHistorico] = useState([]);
  const [loadingHistorico, setLoadingHistorico] = useState(false);

  // Estado para categoria expandida (para ver detalhes)
  const [categoriaExpandida, setCategoriaExpandida] = useState(null);

  // Estado para alertar operadores
  const [showAlertarOperadores, setShowAlertarOperadores] = useState(false);
  const [loteParaAlertar, setLoteParaAlertar] = useState(null);
  const [operadoresAlerta, setOperadoresAlerta] = useState([]); // [{operador_id, operador_nome, nota, selecionado}]
  const [alertando, setAlertando] = useState(false);

  // Estado para análise de tempos
  const [showAnaliseTempos, setShowAnaliseTempos] = useState(false);
  const [ofCompleta, setOfCompleta] = useState(null);
  const [operacoesBase, setOperacoesBase] = useState([]);

  // Estado para editar operações do lote (modal interno)
  const [showEditarOperacoesLote, setShowEditarOperacoesLote] = useState(false);
  const [loteParaEditarOperacoes, setLoteParaEditarOperacoes] = useState(null);
  const [operacoesLote, setOperacoesLote] = useState([]);
  const [loadingOperacoesLote, setLoadingOperacoesLote] = useState(false);
  
  // Estado para edição de quantidade do lote
  const [showEditarQuantidade, setShowEditarQuantidade] = useState(false);
  const [loteParaEditarQuantidade, setLoteParaEditarQuantidade] = useState(null);
  const [novaQuantidade, setNovaQuantidade] = useState('');
  const [salvandoQuantidade, setSalvandoQuantidade] = useState(false);

  // Calcular operações por categoria para mostrar indicadores visuais
  // USA APENAS AS OPERAÇÕES ATRIBUÍDAS À OF, não as globais
  const operacoesPorCategoria = useMemo(() => {
    const map = {};
    // Pegar operações da OF (ofCompleta.operacoes) em vez das globais
    const operacoesOF = ofCompleta?.operacoes || [];
    const opsGlobais = operacoesContext || operacoesBase || [];
    
    operacoesOF.forEach(opOf => {
      // Encontrar a operação base para obter o categoria_id
      const opBase = opsGlobais.find(op => op.id === opOf.operacao_id);
      const catId = opBase?.categoria_id;
      if (catId) {
        if (!map[catId]) {
          map[catId] = [];
        }
        map[catId].push({
          ...opBase,
          ...opOf,
          nome: opBase?.nome || opOf.nome || 'Desconhecida'
        });
      }
    });
    return map;
  }, [ofCompleta, operacoesContext, operacoesBase]);

  useEffect(() => {
    if (ofId) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ofId, categoriasEspeciais]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Usar função do contexto para lotes
      const lotesData = await loadLotesContext(ofId);
      setLotes(lotesData.lotes || []);
      setResumo(lotesData.resumo || {});
      
      // Buscar dados adicionais da OF
      const ofRes = await axios.get(`${API}/ofs/${ofId}`);
      setOfCompleta(ofRes.data);
      
      // Usar operações do contexto se disponíveis, senão buscar
      if (operacoesContext && operacoesContext.length > 0) {
        setOperacoesBase(operacoesContext);
      } else {
        const operacoesRes = await axios.get(`${API}/operacoes`);
        const opsNormalizadas = (operacoesRes.data || []).map(op => ({
          ...op,
          categoria: op.categoria_nome || op.categoria,
          tempo_standard: op.tempo_padrao_segundos || op.tempo_standard || 0
        }));
        setOperacoesBase(opsNormalizadas);
      }
      
      // Usar categorias do contexto se disponíveis, senão buscar
      let todasCategorias = [];
      if (categoriasContext && categoriasContext.length > 0) {
        todasCategorias = categoriasContext;
      } else {
        const categoriasRes = await axios.get(`${API}/categorias`);
        todasCategorias = categoriasRes.data || [];
      }
      setCategorias(todasCategorias);
      
      // Filtrar categorias para o dropdown:
      // - Se a OF tem categorias especiais definidas, mostramos só essas + ARR (sempre).
      // - Caso contrário mostramos TODAS (normais + especiais como ARR e AMOSTRAS) para
      //   que o utilizador possa filtrar por qualquer uma. Eliminar duplicados por id.
      const catArr = todasCategorias.find(c => c.codigo === 'ARR');
      if (categoriasEspeciais && categoriasEspeciais.length > 0) {
        // Se a OF tem categorias especiais, só mostrar essas categorias + ARR como extra
        const catsFiltradas = todasCategorias.filter(c => categoriasEspeciais.includes(c.id));
        if (catArr && !catsFiltradas.find(c => c.id === catArr.id)) catsFiltradas.push(catArr);
        setCategoriasFiltradas(catsFiltradas);
      } else {
        // Sem especiais por OF: mostrar todas (PREP/INT/ACAB/EMB/ARR/AMOSTRAS/...).
        // Dedup por id por segurança e ordenar pela ordem oficial (categorias normais primeiro).
        const seen = new Set();
        const completa = [];
        for (const c of todasCategorias) {
          if (!seen.has(c.id)) {
            seen.add(c.id);
            completa.push(c);
          }
        }
        // Ordenar: especiais (ARR primeiro) ficam ao fim
        completa.sort((a, b) => {
          const aArr = a.codigo === 'ARR' ? 0 : 1;
          const bArr = b.codigo === 'ARR' ? 0 : 1;
          const aSpec = a.especial ? 1 : 0;
          const bSpec = b.especial ? 1 : 0;
          if (aSpec !== bSpec) return aSpec - bSpec; // normais primeiro
          if (aSpec === 1) return aArr - bArr; // entre especiais, ARR primeiro
          return (a.ordem || 0) - (b.ordem || 0);
        });
        setCategoriasFiltradas(completa);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  // Função para construir histórico de um lote
  const construirHistorico = (lote) => {
    const historico = [];
    
    // Adicionar o próprio lote
    historico.push({
      codigo: lote.codigo_lote || `L${lote.numero_lote}`,
      categoria: lote.categoria_nome || 'N/A',
      categoria_codigo: lote.categoria_codigo,
      quantidade: lote.quantidade,
      origem: lote.origem || 'novo',
      data: lote.criado_em,
      status: lote.status,
      observacoes: lote.observacoes
    });
    
    // Rastrear origens
    if (lote.lotes_origem_codigos && lote.lotes_origem_codigos.length > 0) {
      lote.lotes_origem_codigos.forEach(codigo => {
        // Procurar o lote de origem nos lotes
        const loteOrigem = lotes.find(l => 
          (l.codigo_lote === codigo || `L${l.numero_lote}` === codigo)
        );
        if (loteOrigem) {
          historico.unshift({
            codigo: loteOrigem.codigo_lote || `L${loteOrigem.numero_lote}`,
            categoria: loteOrigem.categoria_nome || 'N/A',
            categoria_codigo: loteOrigem.categoria_codigo,
            quantidade: loteOrigem.quantidade,
            origem: loteOrigem.origem || 'novo',
            data: loteOrigem.criado_em,
            status: loteOrigem.status,
            observacoes: loteOrigem.observacoes
          });
        } else {
          // Lote de origem não encontrado, adicionar referência
          historico.unshift({
            codigo: codigo,
            categoria: '?',
            quantidade: '?',
            origem: 'origem',
            data: null,
            status: 'desconhecido'
          });
        }
      });
    }
    
    return historico;
  };

  // Abrir histórico de um lote
  const openHistorico = async (lote) => {
    setLoteHistorico(lote);
    setShowHistorico(true);
    setLoadingHistorico(true);
    setRegistosHistorico([]);
    try {
      const res = await axios.get(`${API}/registos-tempo/lote/${lote.id}`);
      setRegistosHistorico(res.data || []);
    } catch (e) {
      console.warn('Erro ao carregar registos do lote:', e);
    } finally {
      setLoadingHistorico(false);
    }
  };

  // Filtrar lotes por categoria e pesquisa
  const filtrarLotes = (lotesArray) => {
    return lotesArray.filter(lote => {
      // Filtro por categoria
      if (filtroCategoria !== 'todas' && lote.categoria_id !== filtroCategoria) {
        return false;
      }
      // Filtro por pesquisa
      if (pesquisaLote) {
        const termo = pesquisaLote.toLowerCase();
        const codigo = (lote.codigo_lote || `L${lote.numero_lote}`).toLowerCase();
        const categoria = (lote.categoria_nome || '').toLowerCase();
        const obs = (lote.observacoes || '').toLowerCase();
        return codigo.includes(termo) || categoria.includes(termo) || obs.includes(termo);
      }
      return true;
    });
  };

  // Reset do formulário de novo lote

  // Função para abrir diálogo de alertar operadores
  const handleAlertarOperadores = async (lote) => {
    setLoteParaAlertar(lote);
    
    try {
      // Buscar operadores que têm permissão para a categoria deste lote
      const [operadoresRes, ofRes] = await Promise.all([
        axios.get(`${API}/operadores`),
        axios.get(`${API}/ofs/${ofId}`)
      ]);
      
      const operadores = operadoresRes.data || [];
      const of = ofRes.data;
      const categoriaId = lote.categoria_id;
      
      // Buscar operações da OF que pertencem à categoria deste lote
      const operacoesOf = of.operacoes || [];
      const operacoesDoLote = operacoesOf.filter(opOf => {
        // Encontrar a operação completa para verificar a categoria
        const opInfo = operacoesContext.find(o => o.id === opOf.operacao_id);
        return opInfo && opInfo.categoria_id === categoriaId;
      });
      
      // Obter IDs únicos de operadores atribuídos às operações do lote
      const operadoresAtribuidosIds = new Set();
      operacoesDoLote.forEach(opOf => {
        (opOf.operadores_atribuidos || []).forEach(opId => {
          operadoresAtribuidosIds.add(opId);
        });
      });
      
      // Filtrar operadores que estão atribuídos às operações deste lote
      const operadoresRelevantes = operadores.filter(op => 
        operadoresAtribuidosIds.has(op.id)
      );
      
      // Preparar lista de operadores com campos para nota
      setOperadoresAlerta(operadoresRelevantes.map(op => ({
        operador_id: op.id,
        operador_nome: op.nome,
        operador_username: op.username,
        nota: '',
        selecionado: true // Por defeito, todos selecionados
      })));
      
      setShowAlertarOperadores(true);
    } catch (error) {
      console.error('Erro ao carregar operadores:', error);
      toast.error('Erro ao carregar operadores');
    }
  };

  // Função para enviar alertas
  const enviarAlertas = async () => {
    if (!loteParaAlertar) return;
    
    const operadoresSelecionados = operadoresAlerta.filter(op => op.selecionado);
    if (operadoresSelecionados.length === 0) {
      toast.error('Selecione pelo menos um operador');
      return;
    }
    
    setAlertando(true);
    try {
      // Enviar alerta para cada operador selecionado
      const promises = operadoresSelecionados.map(op => 
        axios.post(`${API}/alertas`, {
          titulo: `Nova Tarefa - Lote ${loteParaAlertar.codigo_lote || `L${loteParaAlertar.numero_lote}`}`,
          mensagem: op.nota || `Novo lote disponível na categoria ${loteParaAlertar.categoria_nome || 'N/A'} da OF ${numeroOf}.`,
          prioridade: 'normal',
          destinatarios: 'operadores',
          data_notificacao: new Date().toISOString(),
          destinatario_especifico: op.operador_id,
          tipo: 'sistema',
          dados_of: {
            of_id: ofId,
            of_numero: numeroOf,
            marca: marca,
            lote_id: loteParaAlertar.id,
            lote_codigo: loteParaAlertar.codigo_lote || `L${loteParaAlertar.numero_lote}`,
            lote_quantidade: loteParaAlertar.quantidade,
            categoria_nome: loteParaAlertar.categoria_nome
          }
        })
      );
      
      await Promise.all(promises);
      toast.success(`Alerta enviado para ${operadoresSelecionados.length} operador(es)`);
      setShowAlertarOperadores(false);
      setLoteParaAlertar(null);
      setOperadoresAlerta([]);
    } catch (error) {
      console.error('Erro ao enviar alertas:', error);
      toast.error('Erro ao enviar alertas');
    } finally {
      setAlertando(false);
    }
  };

  // Obter lotes disponíveis para união (de outras categorias, pendentes e aprovados)
  // Permite planeamento antecipado mesmo antes de aprovar os lotes de origem

  // Calcular total de peças selecionadas para união

  // Toggle lote na selecção de união

  // Actualizar quantidade de um lote na união

  // Criar novo lote (tipo novo ou união)

  const handleAprovarLote = async (loteId) => {
    setAprovando(loteId);
    try {
      const response = await aprovarLoteContext(ofId, loteId);
      
      if (response.pdf_url) {
        window.open(`${BACKEND_URL}${response.pdf_url}`, '_blank');
      }
      
      loadData();
      if (onUpdate) onUpdate();
    } catch (error) {
      // Toast já mostrado pelo contexto
    } finally {
      setAprovando(null);
    }
  };

  const handleEliminarLote = async (loteId) => {
    if (!window.confirm('Tem a certeza que deseja eliminar este lote?')) return;
    
    try {
      await deleteLoteContext(ofId, loteId);
      loadData();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao eliminar lote'));
    }
  };

  const handleEditLotePendente = async (loteId, updates) => {
    try {
      await axios.patch(`${API}/ofs/${ofId}/lotes/${loteId}`, updates);
      toast.success('Lote atualizado com sucesso');
      loadData();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao atualizar lote'));
    }
  };

  // Abrir modal para editar operações do lote
  const openEditarOperacoesLote = async (lote) => {
    setLoteParaEditarOperacoes(lote);
    setLoadingOperacoesLote(true);
    setShowEditarOperacoesLote(true);
    
    try {
      // Combinar operacoes_estado do lote com os detalhes das operações base
      const operacoesEstado = lote.operacoes_estado || [];
      
      // Mapear operações com dados completos
      const operacoesCompletas = operacoesEstado.map(opEstado => {
        // Encontrar operação base pelo ID
        const opBase = operacoesContext.find(op => op.id === opEstado.operacao_id);
        
        return {
          ...opEstado,
          codigo: opBase?.codigo || opEstado.operacao_id?.substring(0, 8) || 'N/A',
          nome: opBase?.nome || 'Operação',
          tempo_standard: opBase?.tempo_standard || 0,
          categoria_nome: categorias.find(c => c.id === opBase?.categoria_id)?.nome || '',
          categoria_codigo: categorias.find(c => c.id === opBase?.categoria_id)?.codigo || ''
        };
      });
      
      setOperacoesLote(operacoesCompletas);
    } catch (error) {
      console.error('Erro ao carregar operações do lote:', error);
      setOperacoesLote([]);
    } finally {
      setLoadingOperacoesLote(false);
    }
  };

  // Abrir modal para editar quantidade do lote
  const openEditarQuantidade = (lote) => {
    setLoteParaEditarQuantidade(lote);
    setNovaQuantidade(lote.quantidade.toString());
    setShowEditarQuantidade(true);
  };

  // Salvar nova quantidade do lote
  const handleSalvarQuantidade = async () => {
    if (!loteParaEditarQuantidade || !novaQuantidade) return;
    
    const qtd = parseInt(novaQuantidade);
    if (isNaN(qtd) || qtd <= 0) {
      toast.error('Quantidade inválida. Deve ser um número maior que zero.');
      return;
    }
    
    setSalvandoQuantidade(true);
    try {
      await axios.put(`${API}/ofs/${ofId}/lotes/${loteParaEditarQuantidade.id}/quantidade`, {
        quantidade: qtd
      });
      
      toast.success(`Quantidade atualizada de ${loteParaEditarQuantidade.quantidade} para ${qtd}`);
      setShowEditarQuantidade(false);
      setLoteParaEditarQuantidade(null);
      loadData();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao atualizar quantidade'));
    } finally {
      setSalvandoQuantidade(false);
    }
  };

  // Abrir dialog de avançar lote
  const openAvancarLote = (lote) => {
    setLoteParaAvancar(lote);
    setQuantidadeAvancar(lote.quantidade.toString());
    setCategoriaDestino('');
    setObservacoesAvanco('');
    setErroAvancar('');
    setShowAvancarLote(true);
  };

  // Avançar lote para próxima categoria
  const handleAvancarLote = async () => {
    if (!loteParaAvancar) return;
    setErroAvancar('');
    
    if (!categoriaDestino) {
      setErroAvancar('Selecione a categoria de destino');
      return;
    }

    const quantidade = parseInt(quantidadeAvancar);
    if (!quantidade || quantidade <= 0) {
      setErroAvancar('Insira uma quantidade válida');
      return;
    }
    if (quantidade > loteParaAvancar.quantidade) {
      setErroAvancar(`A quantidade não pode exceder ${loteParaAvancar.quantidade} peças`);
      return;
    }

    setSaving(true);
    try {
      await avancarLoteContext(ofId, loteParaAvancar.id, categoriaDestino, quantidade, observacoesAvanco || '');
      setShowAvancarLote(false);
      setLoteParaAvancar(null);
      setCategoriaDestino('');
      setQuantidadeAvancar('');
      setObservacoesAvanco('');
      setErroAvancar('');
      loadData();
      if (onUpdate) onUpdate();
    } catch (error) {
      // Capturar erro do backend e mostrar no modal
      const errorMsg = error.response?.data?.detail || 'Erro ao avançar lote';
      setErroAvancar(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  // Obter categorias disponíveis para avançar (excluindo a categoria atual do lote)
  const getCategoriasDestino = () => {
    if (!loteParaAvancar) return categorias;
    return categorias.filter(cat => cat.id !== loteParaAvancar.categoria_id);
  };

  // === DIVIDIR LOTE ===
  const openDividirLote = (lote) => {
    setLoteParaDividir(lote);
    setNumeroLotesDivisao(1);
    setQuantidadePorLoteDivisao('');
    setObservacoesDivisao('');
    // Por defeito, usar a próxima categoria
    const categoriaAtual = categorias.find(c => c.id === lote.categoria_id);
    if (categoriaAtual) {
      const indexAtual = categorias.findIndex(c => c.id === lote.categoria_id);
      if (indexAtual < categorias.length - 1) {
        setCategoriaDivisao(categorias[indexAtual + 1].id);
      } else {
        setCategoriaDivisao(lote.categoria_id);
      }
    }
    setErroDividir('');
    setShowDividirLote(true);
  };

  const handleDividirLote = async () => {
    if (!loteParaDividir) return;
    setErroDividir('');
    
    const numLotes = parseInt(numeroLotesDivisao) || 1;
    const qtdPorLote = parseInt(quantidadePorLoteDivisao);
    
    if (!qtdPorLote || qtdPorLote <= 0) {
      setErroDividir('Insira uma quantidade por lote válida');
      return;
    }
    
    const quantidadeTotal = numLotes * qtdPorLote;
    if (quantidadeTotal > loteParaDividir.quantidade) {
      setErroDividir(`Quantidade total (${quantidadeTotal}) excede a disponível (${loteParaDividir.quantidade})`);
      return;
    }

    setSaving(true);
    try {
      await dividirLoteContext(ofId, loteParaDividir.id, categoriaDivisao || null, qtdPorLote, numLotes, quantidadeTotal, observacoesDivisao || '');
      setShowDividirLote(false);
      setLoteParaDividir(null);
      setNumeroLotesDivisao(1);
      setQuantidadePorLoteDivisao('');
      setObservacoesDivisao('');
      setErroDividir('');
      loadData();
      if (onUpdate) onUpdate();
    } catch (error) {
      // Capturar erro do backend e mostrar no modal
      const errorMsg = error.response?.data?.detail || 'Erro ao dividir lote';
      setErroDividir(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  // Gerar PDF do lote
  const handleGerarPDF = (lote) => {
    window.open(getLotePdfUrl(ofId, lote.id), '_blank');
  };

  // === RETROCEDER LOTE ===
  const openRetrocederLote = (lote) => {
    setLoteParaRetroceder(lote);
    setQuantidadeRetroceder(lote.quantidade.toString());
    setCategoriaRetrocesso('');
    setMotivoRetrocesso('');
    setShowRetrocederLote(true);
  };

  const handleRetrocederLote = async () => {
    if (!loteParaRetroceder) return;
    if (!categoriaRetrocesso) {
      toast.error('Selecione a categoria de destino');
      return;
    }
    if (!motivoRetrocesso || motivoRetrocesso.trim().length < 5) {
      toast.error('Indique um motivo válido (mínimo 5 caracteres)');
      return;
    }

    const quantidade = parseInt(quantidadeRetroceder);
    if (!quantidade || quantidade <= 0) {
      toast.error('Insira uma quantidade válida');
      return;
    }
    if (quantidade > loteParaRetroceder.quantidade) {
      toast.error(`A quantidade não pode exceder ${loteParaRetroceder.quantidade} peças`);
      return;
    }

    setSaving(true);
    try {
      await retrocederLoteContext(ofId, loteParaRetroceder.id, categoriaRetrocesso, quantidade, motivoRetrocesso);
      setShowRetrocederLote(false);
      setLoteParaRetroceder(null);
      setCategoriaRetrocesso('');
      setQuantidadeRetroceder('');
      setMotivoRetrocesso('');
      loadData();
      if (onUpdate) onUpdate();
    } catch (error) {
      // Toast já mostrado pelo contexto
    } finally {
      setSaving(false);
    }
  };

  // Função para editar observações do lote
  const openEditObservacoes = (lote) => {
    setLoteParaEditarObs(lote);
    setNovasObservacoes(lote.observacoes || '');
    setShowEditObservacoes(true);
  };

  const handleSalvarObservacoes = async () => {
    if (!loteParaEditarObs) return;
    
    setSaving(true);
    try {
      await updateLoteObsContext(ofId, loteParaEditarObs.id, novasObservacoes || null);
      setShowEditObservacoes(false);
      setLoteParaEditarObs(null);
      setNovasObservacoes('');
      loadData();
    } catch (error) {
      // Toast já mostrado pelo contexto
    } finally {
      setSaving(false);
    }
  };

  // Obter categorias para retrocesso (excluindo a categoria atual)
  const getCategoriasRetrocesso = () => {
    if (!loteParaRetroceder) return categorias;
    return categorias.filter(cat => cat.id !== loteParaRetroceder.categoria_id);
  };

  // === REORDENAR LOTES VIA DRAG AND DROP ===
  const handleReorderLotes = async (loteIds) => {
    try {
      // Atualizar localmente para feedback imediato
      const reordered = loteIds.map(id => lotes.find(l => l.id === id)).filter(Boolean);
      const others = lotes.filter(l => !loteIds.includes(l.id));
      setLotes([...others, ...reordered]);
      
      await axios.post(`${API}/ofs/${ofId}/lotes/reordenar`, { lote_ids: loteIds });
      toast.success('Ordem dos lotes guardada');
    } catch (error) {
      console.error('Erro ao reordenar lotes:', error);
      toast.error('Erro ao guardar ordem dos lotes');
      loadData(); // Recarregar em caso de erro
    }
  };

  // === TRANSFERIR LOTE ===
  const handleDownloadPDF = (pdfUrl) => {
    window.open(`${BACKEND_URL}${pdfUrl}`, '_blank');
  };

  // Separar lotes por estado:
  //  - Aprovados/Terminados → vão para a lista "Lotes Aprovados" (inclui ARR que entram como 'terminado')
  //  - Pendentes → continuam separados
  const lotesAprovados = filtrarLotes(lotes.filter(l => l.status === 'aprovado' || l.status === 'terminado'));
  const lotesPendentes = filtrarLotes(lotes.filter(l => l.status === 'pendente'));
  const todosLotesFiltrados = lotesAprovados.length + lotesPendentes.length;

  // Obter nome da categoria
  const getCategoriaNome = (catId) => {
    const cat = categorias.find(c => c.id === catId);
    return cat ? cat.nome : '';
  };

  const getCategoriaCor = (catCodigo) => {
    // Procurar a categoria para obter a cor definida
    const categoria = categorias.find(c => c.codigo === catCodigo);
    if (categoria?.cor) {
      // Usar a cor definida na categoria
      // Converter cor hex para classes Tailwind com opacidade
      return `border-2`;
    }
    
    // Fallback para cores padrão por código
    const cores = {
      'INT': 'bg-blue-100 text-blue-700 border-blue-300',
      'ACA': 'bg-purple-100 text-purple-700 border-purple-300',
      'EMB': 'bg-green-100 text-green-700 border-green-300',
      'PEC': 'bg-amber-100 text-amber-700 border-amber-300'
    };
    return cores[catCodigo] || 'bg-stone-100 text-stone-700 border-stone-300';
  };

  // Obter cor do border do lote baseado na categoria
  const getLoteBorderColor = (lote) => {
    const categoria = categorias.find(c => c.id === lote.categoria_id);
    if (categoria?.cor) {
      return categoria.cor;
    }
    // Cores padrão por código
    const coresPadrao = {
      'INT': '#3b82f6', // blue-500
      'ACA': '#8b5cf6', // purple-500
      'EMB': '#22c55e', // green-500
      'PEC': '#f59e0b', // amber-500
      'PRE': '#6366f1', // indigo-500
      'PROTO': '#ef4444' // red-500
    };
    return coresPadrao[lote.categoria_codigo] || '#22c55e'; // green-500 como fallback
  };

  const content = (
    <div className="space-y-6">
      {/* Cabeçalho + Filtros + Dashboard */}
      <GestaoLotesHeader
        ofId={ofId}
        numeroOf={numeroOf}
        marca={marca}
        dataEntrega={dataEntrega}
        resumo={resumo}
        quantidadeTotal={quantidadeTotal}
        categoriasFiltradas={categoriasFiltradas}
        operacoesPorCategoria={operacoesPorCategoria}
        lotes={lotes}
        operadores={operadores}
        onClose={onClose}
        filtroCategoria={filtroCategoria}
        setFiltroCategoria={setFiltroCategoria}
        pesquisa={pesquisaLote}
        setPesquisa={setPesquisaLote}
        saving={saving}
        setSaving={setSaving}
        showNovoLote={showNovoLote}
        setShowNovoLote={setShowNovoLote}
        showAnalise={showAnaliseTempos}
        setShowAnalise={setShowAnaliseTempos}
        aprovando={aprovando}
        onUpdate={onUpdate}
        loadData={loadData}
        handleAprovarLote={handleAprovarLote}
        handleEliminarLote={handleEliminarLote}
        handleGerarPDF={handleGerarPDF}
        openAvancarLote={openAvancarLote}
        openDividirLote={openDividirLote}
        openEditarQuantidade={openEditarQuantidade}
        openEditObservacoes={openEditObservacoes}
        openHistorico={openHistorico}
        openRetrocederLote={openRetrocederLote}
        handleAlertarOperadores={handleAlertarOperadores}
        onEditOperacoes={onEditOperacoes}
        createLoteContext={createLoteContext}
        unirLotesContext={unirLotesContext}
        getCategoriaCor={getCategoriaCor}
        onOpenCriarLoteArranjo={() => setShowCriarLoteArranjo(true)}
        onOpenTerceirizar={(lote) => setTerceirizarLote(lote)}
      />

      <CriarLoteArranjoModal
        open={showCriarLoteArranjo}
        onOpenChange={setShowCriarLoteArranjo}
        ofId={ofId}
        onSuccess={() => { loadData(); if (onUpdate) onUpdate(); }}
      />

      <TerceirizacaoLoteModal
        open={!!terceirizarLote}
        onOpenChange={(open) => { if (!open) setTerceirizarLote(null); }}
        ofId={ofId}
        lote={terceirizarLote ? (lotes.find(l => l.id === terceirizarLote.id) || terceirizarLote) : null}
        operacoes={ofCompleta?.operacoes || []}
        onSuccess={async () => {
          await loadData();
          if (onUpdate) onUpdate();
        }}
      />


      {/* Lotes Pendentes — só status=pendente (excluindo aprovados/terminados) */}
      <LotesPendentesLista
        lotes={filtrarLotes(lotes.filter(l => l.status === 'pendente'))}
        aprovando={aprovando}
        ofId={ofId}
        handleAprovarLote={handleAprovarLote}
        handleEliminarLote={handleEliminarLote}
        handleGerarPDF={handleGerarPDF}
        openEditObservacoes={openEditObservacoes}
        openHistorico={openHistorico}
        getCategoriaCor={getCategoriaCor}
        onEditLotePendente={handleEditLotePendente}
      />

      <LotesAprovadosLista
        lotes={filtrarLotes(lotes.filter(l => l.status === 'aprovado' || l.status === 'terminado'))}
        aprovando={aprovando}
        ofId={ofId}
        operadores={operadores}
        handleAlertarOperadores={handleAlertarOperadores}
        handleEliminarLote={handleEliminarLote}
        handleGerarPDF={handleGerarPDF}
        onEditOperacoes={onEditOperacoes}
        onEditOperacoesLote={openEditarOperacoesLote}
        openAvancarLote={openAvancarLote}
        openDividirLote={openDividirLote}
        openEditObservacoes={openEditObservacoes}
        openHistorico={openHistorico}
        openRetrocederLote={openRetrocederLote}
        openEditarQuantidade={openEditarQuantidade}
        getCategoriaCor={getCategoriaCor}
        onReorderLotes={handleReorderLotes}
      />

      {lotes.length === 0 && !loading && (
        <div className="text-center py-12 text-stone-500">
          <Package className="h-16 w-16 mx-auto mb-4 opacity-30" />
          <p className="text-lg font-medium">Nenhum lote criado</p>
          <p className="text-sm">Comece por criar o primeiro lote para esta OF</p>
        </div>
      )}

      {/* Modal Avançar Lote */}
      <ModalAvancarLote
        open={showAvancarLote}
        onOpenChange={(open) => {
          setShowAvancarLote(open);
          if (!open) {
            setLoteParaAvancar(null);
            setCategoriaDestino('');
            setQuantidadeAvancar('');
            setObservacoesAvanco('');
            setErroAvancar('');
          }
        }}
        lote={loteParaAvancar}
        categoriaDestino={categoriaDestino}
        setCategoriaDestino={setCategoriaDestino}
        quantidadeAvancar={quantidadeAvancar}
        setQuantidadeAvancar={setQuantidadeAvancar}
        observacoesAvanco={observacoesAvanco}
        setObservacoesAvanco={setObservacoesAvanco}
        erroAvancar={erroAvancar}
        saving={saving}
        categoriasDestino={getCategoriasDestino()}
        getCategoriaCor={getCategoriaCor}
        onConfirm={handleAvancarLote}
      />

      {/* Modal Dividir Lote */}
      <ModalDividirLote
        open={showDividirLote}
        onOpenChange={(open) => {
          setShowDividirLote(open);
          if (!open) {
            setLoteParaDividir(null);
            setNumeroLotesDivisao(1);
            setQuantidadePorLoteDivisao('');
            setObservacoesDivisao('');
            setErroDividir('');
          }
        }}
        lote={loteParaDividir}
        categoriaDivisao={categoriaDivisao}
        setCategoriaDivisao={setCategoriaDivisao}
        numeroLotesDivisao={numeroLotesDivisao}
        setNumeroLotesDivisao={setNumeroLotesDivisao}
        quantidadePorLoteDivisao={quantidadePorLoteDivisao}
        setQuantidadePorLoteDivisao={setQuantidadePorLoteDivisao}
        observacoesDivisao={observacoesDivisao}
        setObservacoesDivisao={setObservacoesDivisao}
        erroDividir={erroDividir}
        saving={saving}
        categoriasFiltradas={categoriasFiltradas}
        getCategoriaCor={getCategoriaCor}
        onConfirm={handleDividirLote}
      />

      {/* Modal Retroceder Lote */}
      <ModalRetrocederLote
        open={showRetrocederLote}
        onOpenChange={(open) => {
          setShowRetrocederLote(open);
          if (!open) {
            setLoteParaRetroceder(null);
            setCategoriaRetrocesso('');
            setQuantidadeRetroceder('');
            setMotivoRetrocesso('');
          }
        }}
        lote={loteParaRetroceder}
        categoriaRetrocesso={categoriaRetrocesso}
        setCategoriaRetrocesso={setCategoriaRetrocesso}
        quantidadeRetroceder={quantidadeRetroceder}
        setQuantidadeRetroceder={setQuantidadeRetroceder}
        motivoRetrocesso={motivoRetrocesso}
        setMotivoRetrocesso={setMotivoRetrocesso}
        saving={saving}
        categoriasRetrocesso={getCategoriasRetrocesso()}
        getCategoriaCor={getCategoriaCor}
        onConfirm={handleRetrocederLote}
      />


      <GestaoLotesHistorico
        open={showHistorico}
        onOpenChange={(v) => { setShowHistorico(v); if (!v) { setLoteHistorico(null); setRegistosHistorico([]); } }}
        lote={loteHistorico}
        registos={registosHistorico}
        loading={loadingHistorico}
        construirHistorico={construirHistorico}
        getCategoriaCor={getCategoriaCor}
      />


      <GestaoLotesAlertarOps
        open={showAlertarOperadores}
        onOpenChange={(v) => { setShowAlertarOperadores(v); if (!v) { setLoteParaAlertar(null); setOperadoresAlerta([]); } }}
        lote={loteParaAlertar}
        operadores={operadoresAlerta}
        onSetOperadores={setOperadoresAlerta}
        onEnviar={enviarAlertas}
        alertando={alertando}
      />

      {/* Modal de Análise de Tempos */}
      <AnaliseTemposOF
        open={showAnaliseTempos}
        onOpenChange={setShowAnaliseTempos}
        of={ofCompleta}
        operacoesBase={operacoesBase}
        categorias={categorias}
        quantidade={quantidadeTotal || ofCompleta?.quantidade}
      />

      {/* Modal Editar Observações do Lote */}
      <Dialog open={showEditObservacoes} onOpenChange={(open) => {
        setShowEditObservacoes(open);
        if (!open) {
          setLoteParaEditarObs(null);
          setNovasObservacoes('');
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5 text-primary" />
              Editar Observações
            </DialogTitle>
            <DialogDescription>
              Lote: <strong>{loteParaEditarObs?.codigo_lote || `L${loteParaEditarObs?.numero_lote}`}</strong>
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Textarea
              placeholder="Escreva aqui as observações do lote..."
              value={novasObservacoes}
              onChange={(e) => setNovasObservacoes(e.target.value)}
              rows={5}
              className="resize-none text-base"
            />
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowEditObservacoes(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSalvarObservacoes} disabled={saving}>
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <ModalOperacoesLote
        open={showEditarOperacoesLote}
        onOpenChange={(v) => { setShowEditarOperacoesLote(v); if (!v) { setLoteParaEditarOperacoes(null); setOperacoesLote([]); } }}
        lote={loteParaEditarOperacoes ? (lotes.find(l => l.id === loteParaEditarOperacoes.id) || loteParaEditarOperacoes) : null}
        operacoes={operacoesLote}
        loading={loadingOperacoesLote}
        operadores={operadores}
        onEditOperacoes={onEditOperacoes}
        ofId={ofId}
        onUpdated={async () => {
          if (typeof loadLotesContext === 'function') {
            try {
              const data = await loadLotesContext(ofId);
              setLotes(data.lotes || []);
            } catch (e) { /* ignore */ }
          }
          if (typeof onUpdate === 'function') onUpdate();
        }}
      />

      {/* Modal Editar Quantidade do Lote */}
      <ModalEditarQuantidade
        open={showEditarQuantidade}
        onOpenChange={(open) => {
          setShowEditarQuantidade(open);
          if (!open) {
            setLoteParaEditarQuantidade(null);
            setNovaQuantidade('');
          }
        }}
        lote={loteParaEditarQuantidade}
        novaQuantidade={novaQuantidade}
        setNovaQuantidade={setNovaQuantidade}
        salvandoQuantidade={salvandoQuantidade}
        onConfirm={handleSalvarQuantidade}
      />
    </div>
  );

  if (loading) {
    return (
      <div className="fixed inset-y-0 left-64 right-0 bg-stone-100 z-40 flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-navy" />
      </div>
    );
  }

  // Fullscreen mode - respects sidebar
  if (fullscreen) {
    return (
      <div className="fixed inset-y-0 left-64 right-0 bg-stone-100 z-40 overflow-y-auto">
        <div className="max-w-7xl mx-auto p-6">
          {content}
        </div>
      </div>
    );
  }

  // Dialog mode (fallback)
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[95vh] overflow-y-auto">
        {content}
      </DialogContent>
    </Dialog>
  );
}
